import React from "react";
import { useForm, FormProvider } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { PaziaModal } from "components/molecules";
import { PaziaButton } from "components/atoms";
import { ModalHeader } from "../../../components/templates/Modals/ModalHeader";
import { ModalPadding } from "../../../components/templates/Modals/ModalPadding";
import { BasePlaceScheduleForm } from "./BasePlaceScheduleForm";
import { courseschedulesApi } from "api";
import { usePlacesOptions } from "api/hooks";
import { COURSE_PLACE_TYPE } from "consts/courseScheduleType";

interface BulkEditPlacesModalProps {
  isOpen: boolean;
  onCloseRequest: () => void;
  courseId: number;
  selectedScheduleIds: number[];
  onSuccess: () => void;
}

const bulkEditPlacesSchema = yup.object({
  placeType: yup.string().required("Typ miejsca jest wymagany"),
  place: yup.string().when("placeType", {
    is: (placeType: string) =>
      placeType === COURSE_PLACE_TYPE.ONSITE.toString() || placeType === COURSE_PLACE_TYPE.USER_CHOICE.toString(),
    then: (schema) => schema.required("Miejsce jest wymagane"),
  }),
});

type BulkEditPlacesFormData = yup.InferType<typeof bulkEditPlacesSchema>;

export const BulkEditPlacesModal = ({
  isOpen,
  onCloseRequest,
  courseId,
  selectedScheduleIds,
  onSuccess,
}: BulkEditPlacesModalProps) => {
  const [isLoading, setIsLoading] = React.useState(false);

  const methods = useForm<BulkEditPlacesFormData>({
    resolver: yupResolver(bulkEditPlacesSchema),
    defaultValues: {
      placeType: "",
      place: "",
    },
  });

  const { handleSubmit, reset, watch } = methods;
  const { loading: loadingPlaces, options: placesOptions } = usePlacesOptions();

  const onSubmit = async (data: BulkEditPlacesFormData) => {
    try {
      setIsLoading(true);

      const requestData = {
        courseScheduleIds: selectedScheduleIds,
        placeType: parseInt(data.placeType),
        placeId: data.place ? parseInt(data.place) : undefined,
      };

      await courseschedulesApi.bulkUpdatePlaces(courseId, requestData);

      onSuccess();
      onCloseRequest();
      reset();
    } catch (error) {
      console.error("Error updating places:", error);
      // TODO: Add proper error handling/notification
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    reset();
    onCloseRequest();
  };

  const selectedPlaceType = watch("placeType");
  const isOnSite =
    selectedPlaceType === COURSE_PLACE_TYPE.ONSITE.toString() ||
    selectedPlaceType === COURSE_PLACE_TYPE.USER_CHOICE.toString();

  return (
    <PaziaModal id="bulk-edit-places-modal" isOpen={isOpen} onCloseRequest={handleClose} size="lg">
      <FormProvider {...methods}>
        <ModalPadding form onSubmit={handleSubmit(onSubmit)}>
          <ModalHeader>Zmień miejsce dla wybranych zajęć ({selectedScheduleIds.length})</ModalHeader>
          <div className="grid grid-cols-12 gap-4">
            <BasePlaceScheduleForm />
          </div>

          <div className="mt-6 flex justify-end gap-3">
            <PaziaButton type="secondary" onClick={handleClose} disabled={isLoading}>
              Anuluj
            </PaziaButton>
            <PaziaButton type="primary" btnType="submit" disabled={isLoading || loadingPlaces} processing={isLoading}>
              Zapisz zmiany
            </PaziaButton>
          </div>
        </ModalPadding>
      </FormProvider>
    </PaziaModal>
  );
};
